import { createFileRoute, Link } from "@tanstack/react-router";

import { useEffect, useRef, useState } from "react";
import { AppShell } from "@/components/app-shell";
import { PostCard, type FeedPost } from "@/components/post-card";
import { fetchFeed, type FeedSort } from "@/lib/posts";
import { useAuth } from "@/lib/auth-context";
import { RefreshCw, WifiOff } from "lucide-react";
import { cn } from "@/lib/utils";
import { UserAvatar } from "@/components/user-avatar";
import { CreatePostDialog } from "@/components/create-post-dialog";
import { HotspotAlertStrip } from "@/components/hotspot-alert-strip";
import { supabase } from "@/integrations/supabase/client";
import { FeedSkeleton } from "@/components/feed-skeleton";
import { LensEmptyState } from "@/components/lens-empty-state";
import { PublicLanding } from "@/components/public-landing";
import { fetchFeedSources } from "@/lib/follows";
import { AnimatePresence, motion } from "framer-motion";


export const Route = createFileRoute("/")({
  component: HomeRoute,
  head: () => {
    const title = "Xamseen — See it. Share it. Shape it.";
    const description =
      "Xamseen is a global community platform where people connect, share what is happening around them, report issues, discover solutions, join projects and turn participation into real-world impact.";
    const image = "https://xamseen.com/icon-512.png";
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "website" },
        { property: "og:url", content: "https://xamseen.com/" },
        { property: "og:site_name", content: "Xamseen" },
        { property: "og:image", content: image },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
        { name: "twitter:image", content: image },
      ],
      links: [{ rel: "canonical", href: "https://xamseen.com/" }],
    };
  },

});

type Tab = "your-feed" | "for-you" | "latest";
type Source = "all" | "people" | "communities";

/**
 * "/" has two worlds: the public Xamseen landing page for visitors and Lens for
 * signed-in members. While the session resolves we keep the landing markup
 * unless a stored session hints the visitor is a member (avoids a flash).
 */
function HomeRoute() {
  const { user, loading } = useAuth();
  // The first client render must match the server's markup, so the stored-session
  // hint is only consulted after hydration.
  const [hydrated, setHydrated] = useState(false);
  useEffect(() => setHydrated(true), []);
  if (user) return <FeedPage />;
  if (hydrated && loading && hasStoredSession()) return <div className="min-h-screen bg-background" />;
  return <PublicLanding />;
}

function hasStoredSession() {
  if (typeof window === "undefined") return false;
  try {
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const key = window.localStorage.key(i);
      if (key && key.startsWith("sb-") && key.endsWith("-auth-token")) return true;
    }
  } catch {
    return false;
  }
  return false;
}

function FeedPage() {
  const { user, profile } = useAuth();
  const [tab, setTab] = useState<Tab>("your-feed");
  const [source, setSource] = useState<Source>("all");
  const [sources, setSources] = useState<{ authorIds: string[]; communityIds: string[] } | null>(null);
  const [posts, setPosts] = useState<FeedPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [composeOpen, setComposeOpen] = useState(false);
  const [newCount, setNewCount] = useState(0);
  const [reloadKey, setReloadKey] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [pageError, setPageError] = useState(false);
  const [offline, setOffline] = useState(false);
  const PAGE_SIZE = 20;

  const sort: FeedSort = tab === "for-you" ? "trending" : "latest";
  // "Your feed" only shows the people you follow and the communities you joined.
  const following = tab === "your-feed";
  const scope = following && sources
    ? {
        authorIds: source === "communities" ? [] : sources.authorIds,
        communityIds: source === "people" ? [] : sources.communityIds,
      }
    : {};
  const ranked = sort === "trending";

  // Network awareness — drives the offline banner and auto-recovery.
  useEffect(() => {
    const sync = () => setOffline(typeof navigator !== "undefined" && !navigator.onLine);
    sync();
    const back = () => { sync(); setReloadKey((k) => k + 1); };
    window.addEventListener("online", back);
    window.addEventListener("offline", sync);
    return () => {
      window.removeEventListener("online", back);
      window.removeEventListener("offline", sync);
    };
  }, []);

  // Who and what this member follows — refreshed when they follow someone new.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    fetchFeedSources(user.id).then((s) => { if (!cancelled) setSources(s); });
    return () => { cancelled = true; };
  }, [user?.id, reloadKey]);

  useEffect(() => {
    let cancelled = false;
    if (following && !sources) return;
    (async () => {
      // Keep the existing posts on screen while refetching so a slow network
      // never blanks a feed the user was already reading.
      setLoading(posts.length === 0);
      setError(null);
      setHasMore(true);
      try {
        const data = await fetchFeed({ sort, userId: user?.id, limit: PAGE_SIZE, ...scope });
        if (!cancelled) {
          setPosts(data);
          setNewCount(0);
          setHasMore(data.length === PAGE_SIZE);
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : "Could not load the feed.");
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, source, sources, user?.id, reloadKey]);

  // Live: count posts created after this feed was loaded
  useEffect(() => {
    const ch = supabase
      .channel("feed-live")
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "posts" }, (payload) => {
        const row = payload.new as { author_id?: string };
        if (row.author_id && row.author_id === user?.id) return;
        setNewCount((n) => n + 1);
      })
      .subscribe();
    return () => { void supabase.removeChannel(ch); };
  }, [user?.id]);

  const showNewPosts = () => {
    setNewCount(0);
    setReloadKey((k) => k + 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const loadMore = async () => {
    if (loadingMore || !hasMore || posts.length === 0) return;
    setLoadingMore(true);
    setPageError(false);
    try {
      const next = ranked
        ? await fetchFeed({ sort, userId: user?.id, limit: PAGE_SIZE, offset: posts.length, ...scope })
        : await fetchFeed({ sort, userId: user?.id, limit: PAGE_SIZE, cursor: posts[posts.length - 1].created_at, ...scope });
      const seen = new Set(posts.map(p => p.id));
      const fresh = next.filter(p => !seen.has(p.id));
      setPosts(prev => [...prev, ...fresh]);
      if (next.length < PAGE_SIZE) setHasMore(false);
    } catch {
      setPageError(true);
    } finally {
      setLoadingMore(false);
    }
  };

  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || pageError) return;
    const io = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting) loadMore();
    }, { rootMargin: "800px 0px" });
    io.observe(el);
    return () => io.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [posts, hasMore, loadingMore, tab, pageError]);


  return (
    <AppShell>
      <div className="-mx-4 sm:-mx-6 -my-4 md:-my-6">
        {/* Sticky header with tabs */}
        <header className="sticky top-[calc(env(safe-area-inset-top)+3.5rem)] z-20 border-b border-border bg-background/85 backdrop-blur md:top-0">
          <div className="flex h-11 items-center px-4">
            <h1 className="font-display text-[17px] font-bold tracking-tight">What's happening</h1>
          </div>
          <div className="grid grid-cols-3">
            <TabButton active={tab === "your-feed"} onClick={() => setTab("your-feed")}>Your feed</TabButton>
            <TabButton active={tab === "for-you"} onClick={() => setTab("for-you")}>Around you</TabButton>
            <TabButton active={tab === "latest"} onClick={() => setTab("latest")}>Latest</TabButton>
          </div>
          <AnimatePresence initial={false}>
            {following && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.24, ease: [0.22, 0.61, 0.36, 1] }}
                className="overflow-hidden"
              >
                <div className="flex gap-2 px-4 pb-3">
                  {(["all", "people", "communities"] as Source[]).map((s) => (
                    <button
                      key={s}
                      onClick={() => setSource(s)}
                      aria-pressed={source === s}
                      className={cn(
                        "min-h-9 rounded-full border px-3.5 text-[13px] font-medium transition-colors",
                        source === s
                          ? "border-foreground bg-foreground text-background"
                          : "border-border text-muted-foreground hover:bg-muted",
                      )}
                    >
                      {s === "all" ? "Everything" : s === "people" ? "People" : "Communities"}
                    </button>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </header>


        {/* Nearby alerts */}
        <div className="px-4 pt-2">
          <HotspotAlertStrip />
        </div>



        {/* Inline composer */}
        {user && (
          <button
            onClick={() => setComposeOpen(true)}
            className="flex w-full items-center gap-3 border-b border-border px-4 py-3 text-left transition-colors hover:bg-accent/30"
          >
            <UserAvatar
              name={profile?.full_name}
              username={profile?.username}
              avatarUrl={profile?.avatar_url}
              size={40}
              className="h-10 w-10 shrink-0"
            />
            <div className="flex min-w-0 flex-1 items-center justify-between gap-3">
              <span className="truncate text-[17px] text-muted-foreground">What's happening?</span>
              <span className="rounded-full bg-primary px-4 py-1.5 text-sm font-semibold text-primary-foreground">
                Post
              </span>
            </div>
          </button>
        )}

        {/* Live new-posts pill */}
        {newCount > 0 && !loading && (
          <div className="sticky top-[calc(env(safe-area-inset-top)+7rem)] z-20 flex justify-center py-2 md:top-14">
            <button
              onClick={showNewPosts}
              className="rounded-full bg-primary px-4 py-1.5 text-sm font-semibold text-primary-foreground shadow-lg"
            >
              Show {newCount} new post{newCount === 1 ? "" : "s"}
            </button>
          </div>
        )}

        {/* Offline banner */}
        {offline && (
          <div className="flex items-center gap-2 border-b border-border bg-muted/50 px-4 py-2 text-sm text-muted-foreground">
            <WifiOff className="h-4 w-4" />
            You're offline — showing the last posts we loaded.
          </div>
        )}

        {/* Feed */}
        {loading ? (
          <FeedSkeleton />
        ) : error && posts.length === 0 ? (
          <div className="px-6 py-16 text-center">
            <p className="text-lg font-semibold">We couldn't load the feed</p>
            <p className="mx-auto mt-1 max-w-sm text-sm text-muted-foreground">
              {offline ? "You appear to be offline. Reconnect and try again." : "The network dropped out. Give it another try."}
            </p>
            <button
              onClick={() => setReloadKey((k) => k + 1)}
              className="mt-4 inline-flex items-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground"
            >
              <RefreshCw className="h-4 w-4" /> Try again
            </button>
          </div>
        ) : posts.length === 0 && following ? (
          <div className="px-6 py-16 text-center">
            <p className="font-display text-lg font-bold">Your feed is quiet</p>
            <p className="mx-auto mt-1.5 max-w-sm text-sm text-muted-foreground">
              Follow people and join communities, and everything they share lands here.
            </p>
            <Link
              to="/search"
              className="mt-5 inline-flex min-h-11 items-center rounded-full bg-foreground px-5 text-sm font-semibold text-background"
            >
              Find people to follow
            </Link>
          </div>
        ) : posts.length === 0 ? (
          <LensEmptyState
            place={profile?.city ?? null}
            onCompose={user ? () => setComposeOpen(true) : undefined}
            onSeeLatest={tab === "for-you" ? () => setTab("latest") : undefined}
          />

        ) : (
          <>
            {posts.map((p) => (
              <PostCard key={p.id} post={p} onChanged={() => setTab(tab)} />
            ))}
            {hasMore && !pageError && (
              <div ref={sentinelRef} className="py-2">
                {loadingMore
                  ? <FeedSkeleton count={2} />
                  : <p className="py-6 text-center text-xs text-muted-foreground">Loading more…</p>}
              </div>
            )}
            {pageError && (
              <div className="flex flex-col items-center gap-3 py-8">
                <p className="text-sm text-muted-foreground">Couldn't load more posts.</p>
                <button
                  onClick={() => { setPageError(false); void loadMore(); }}
                  className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-1.5 text-sm font-semibold"
                >
                  <RefreshCw className="h-4 w-4" /> Retry
                </button>
              </div>
            )}
            {!hasMore && posts.length > 10 && (
              <p className="py-8 text-center text-xs text-muted-foreground">You're all caught up.</p>
            )}
          </>
        )}
      </div>

      {user && <CreatePostDialog open={composeOpen} onOpenChange={setComposeOpen} />}
    </AppShell>
  );
}

function TabButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "relative flex h-11 items-center justify-center text-[15px] font-medium transition-colors hover:bg-accent/40",
        active ? "text-foreground" : "text-muted-foreground"
      )}
    >
      <span className="relative py-3">
        {children}
        {active && <span className="absolute inset-x-0 -bottom-px h-1 rounded-full bg-primary" />}
      </span>
    </button>
  );
}
